import React, {useMemo} from 'react';

import {Fade, List, ListItem, ListItemIcon, ListItemText, Popover, Switch} from '@mui/material';
import HelpCenterOutlined from '@mui/icons-material/HelpCenterOutlined';
import LibraryBooksOutlined from '@mui/icons-material/LibraryBooksOutlined';
import MailOutlineOutlined from '@mui/icons-material/MailOutlineOutlined';

export default function SearchRangeModal({ tuneButtonElement, isOpenSearchRangeModal, isOnboardingScreen, botList, currentBotId, closeSearchRangeModal, handleChangeCurrentBot }) {
    const allowedTypeList = ['DOCUMENT', 'EMAIL', 'FAQ']; // 검색 범위 설정 모달에 표시할 봇 타입 목록

    const filteredBotList = useMemo(
        () => (botList ?? []).filter(bot => allowedTypeList.includes((bot.type || '').toUpperCase())),
        [botList]
    );

    // type === DATABASE인 봇 id 추출
    const databaseBotId = useMemo(() => {
        const databasebot = (botList ?? []).find(
            (bot) => (bot.type || '').toUpperCase() === 'DATABASE'
        );
        
        return databasebot?.id ?? null;
    }, [botList]);

    // 봇 변경하는 함수 : 활성화 -> 해당 봇으로 변경, 비활성화 -> 데이터베이스로 변경
    const changeCurrentBot = (e, bot) => { // bot : { id, type }
        e.stopPropagation();
        
        const checked = e.target.checked;

        if (checked) { // 켜기
            handleChangeCurrentBot(bot);
        } else { // 끄기, DATABASE로 설정
            handleChangeCurrentBot({ id: databaseBotId, type: 'DATABASE' });
        }
    };

    return (
        <Popover
            open={isOpenSearchRangeModal}
            anchorEl={tuneButtonElement}
            anchorOrigin={{ vertical: isOnboardingScreen ? 'bottom' : 'top', horizontal: 'left' }} // 버튼 바로 위 or 아래 위치
            transformOrigin={{ vertical: isOnboardingScreen ? 'top' : 'bottom', horizontal: 'left' }}
            TransitionComponent={Fade} // Grow → Fade (transform 충돌 방지)
            marginThreshold={0}        // 기본 마진 제거
            slotProps={{
                paper: {               // paper는 popover에 내장되어 있는 구조
                    elevation: 0,      // 그림자 제거(1)
                    sx: {
                        width: 276,
                        maxHeight: 178,
                        overflowY: 'auto',
                        p: 1,           // padding: 8px
                        boxSizing: 'border-box',
                        border: '1px solid',
                        borderColor: 'grey.400',
                        borderRadius: '4px',
                        boxShadow: 'none', // 그림자 제거(2)
                        transform: `translateY(${ isOnboardingScreen ? 5 : -5 }px)` // 버튼과 5px 간격
                    },
                },
            }}
            onClose={closeSearchRangeModal}
        >
            <List
                sx={{ 
                    '& .MuiListItem-root': { width: 260, height: 54, padding: '8px 16px' },
                    '& .MuiListItemIcon-root': { minWidth: 36, color: '#0000008A' },
                    '& .MuiListItemText-primary': { fontSize: 16, fontWeight: 400 } 
                }}
                dense
                disablePadding
            >
                {filteredBotList.map(({ id, type }) => {
                    const { Icon, text } = getBotInfo(type);
                        
                    const isActiveSwitch = currentBotId === id;

                    return (
                        <ListItem
                            key={id}
                            disableGutters // 기본 패딩 제거
                        >
                            <ListItemIcon>
                                <Icon />
                            </ListItemIcon>
                            <ListItemText primary={text} />
                            <div style={{ flex: '0 0 58px', display: 'flex', justifyContent: 'flex-end' }}>
                                <Switch
                                    edge='end'
                                    color='primary'
                                    state='enabled'
                                    checked={isActiveSwitch}
                                    onChange={(e) => changeCurrentBot(e, { id, type })}                                        
                                />  
                            </div>
                        </ListItem>
                    );
                })}
            </List>
        </Popover>
    );
}

const BOT_ICON = {
    DOCUMENT: LibraryBooksOutlined,
    EMAIL: MailOutlineOutlined,
    FAQ: HelpCenterOutlined
};

const BOT_TEXT = {
    DOCUMENT: '문서 검색',
    EMAIL: 'GMail 검색',
    FAQ: 'FAQ 검색'
};

function getBotInfo(type) {
    const Icon = BOT_ICON[type];
    const text = BOT_TEXT[type];
    return { Icon, text };
}
